import { computedFrom } from "aurelia-binding";
import { transient } from "aurelia-dependency-injection";
import { BaseMepFilterViewModel, MepDashboardRequest, MepLookupItem } from "../../../../../../../../models/mep/mep.models";
import { NotificationClientService, NotificationType } from "../../../../../../../../services/basics/notification-client.service";
import { MepLookupService } from "../../../../../../../../services/mep/mep-lookup.service";
import { MepLookupFiltersHandler } from "../../../../common-vm/mep-lookup-filters-handler";
import * as Enumerable from "linq";
import { useView } from "aurelia-framework";

@transient()
@useView('views/pages/modules/mep/tabs/mep-dashboard/partials/mep-dashboard-filter/mep-dashboard-filter.html')
export class MepDashboardFilter extends BaseMepFilterViewModel {

    @computedFrom('lookupFiltersHandler', 'lookupFiltersHandler.emissionTypes')
    get emissionTypes(): MepLookupItem[] {
        return this.lookupFiltersHandler.emissionTypes;
    }

    @computedFrom('lookupFiltersHandler', 'lookupFiltersHandler.pointsOfInterests')
    get pointsOfInterests(): MepLookupItem[] {
        return this.lookupFiltersHandler.pointsOfInterests;
    }

    @computedFrom('lookupFiltersHandler', 'lookupFiltersHandler.operationModes')
    get operationModes(): MepLookupItem[] {
        return this.lookupFiltersHandler.operationModes;
    }

    @computedFrom('lookupFiltersHandler', 'lookupFiltersHandler.cargoTypes')
    get cargoTypes(): MepLookupItem[] {
        return this.lookupFiltersHandler.cargoTypes;
    }

    @computedFrom('lookupFiltersHandler', 'lookupFiltersHandler.vesselTypeGroups')
    get vesselTypeGroups(): MepLookupItem[] {
        return this.lookupFiltersHandler.vesselTypeGroups;
    }

    @computedFrom('lookupFiltersHandler', 'lookupFiltersHandler.isLookupDataRefreshing')
    get isLookupDataRefreshing(): boolean {
        return this.lookupFiltersHandler.isLookupDataRefreshing;
    }

    @computedFrom('lastRequest', 'previousRequest')
    get hasRequestChanged(): boolean {
        return JSON.stringify(this.lastRequest) != JSON.stringify(this.previousRequest);

    }

    refreshDataFunc: (request: MepDashboardRequest) => Promise<string[]>;

    //Emission Type Filter
    selectedEmissionTypes: MepLookupItem[] = [];
    //Point of Interest Filter
    selectedPointsOfInterest: MepLookupItem[] = [];
    //Operation Mode Filter
    selectedOperationModes: MepLookupItem[] = [];
    //Cargo Type Filter
    selectedCargoTypes: MepLookupItem[] = [];
    //Vessel Type Filter
    selectedVesselTypeGroups: MepLookupItem[] = [];
    supportsCargoType: boolean;

    private lastRequest: MepDashboardRequest;
    private previousRequest: MepDashboardRequest;

    applyFiltersFunc: () => void;

    @computedFrom("isRefreshingData")
    get emissionTypeCategoryIds(): number[] {
        return Enumerable.from(this.selectedEmissionTypes.length == 0
            ? this.emissionTypes
            : this.selectedEmissionTypes)
            .where(x => x.additionalData != null)
            .select(x => x.additionalData.emissionTypeCategoryId)
            .distinct()
            .toArray();
    }

    constructor(portId: number, supportsCargoType, refreshDataFunc: (request: MepDashboardRequest) => Promise<string[]>,
        private notificationService: NotificationClientService,
        mepLookupService: MepLookupService) {
        super();
        this.portId = portId;
        this.supportsCargoType = supportsCargoType;
        this.refreshDataFunc = refreshDataFunc;

        this.applyFiltersFunc = () => this.applyAllFilters();
        this.lookupFiltersHandler = new MepLookupFiltersHandler(portId, this.applyFiltersFunc, mepLookupService);
    }

    initLookups() {
        return this.lookupFiltersHandler.initLookups();
    }

    getLastRequest(): MepDashboardRequest {
        return this.lastRequest;
    }

    changeEmissionTypesFilter(selectedEmissionTypeIds: number[]) {
        this.selectedEmissionTypes = MepLookupItem.getSelectedItems(this.emissionTypes, selectedEmissionTypeIds);
    }

    changePointOfInterestsFilter(selectedPointOfInterestIds: number[]) {
        this.selectedPointsOfInterest = MepLookupItem.getSelectedItems(this.pointsOfInterests, selectedPointOfInterestIds);
    }

    changeVesselTypesFilter(selectedVesselTypeIds: number[]) {
        this.selectedVesselTypeGroups = MepLookupItem.getSelectedItems(this.vesselTypeGroups, selectedVesselTypeIds);
    }

    changeOperationModesFilter(selectedOperationModeIds: number[]) {
        this.selectedOperationModes = MepLookupItem.getSelectedItems(this.operationModes, selectedOperationModeIds);
    }

    changeCargoTypeFilter(selectedCargoTypeIds: number[]) {
        this.selectedCargoTypes = MepLookupItem.getSelectedItems(this.cargoTypes, selectedCargoTypeIds);
    }

    changeCargoTypeGroupFilter(selectedCargoTypeGroups: string[]) {
        const selectedCargoTypeIds = this.cargoTypes
            .filter(x => selectedCargoTypeGroups.indexOf(x.additionalData.cargoTypeGroup) > -1)
            .map(x => x.id);
        this.selectedCargoTypes = MepLookupItem.getSelectedItems(this.cargoTypes, selectedCargoTypeIds);
    }

    onSelectedMonthsChanged() {
        if (this.isRefreshingData || this.isLookupDataRefreshing)
            return;

        const refreshLookupsPromise = this.lookupFiltersHandler.onSelectedMonthsChanged();
        if (refreshLookupsPromise) {
            refreshLookupsPromise.then(() => {
                this.resetFiltersToDefault();
            });
        }
    }

    isRefreshingData: boolean;
    applyAllFilters() {
        if (this.isRefreshingData || this.isLookupDataRefreshing)
            return;

        if (this.refreshDataFunc) {
            this.isRefreshingData = true;

            //save the last request so that other view models can use this to do other queries 
            //we need JSON.parse to create deep copy of object otherwise lastRequest is shallow copy and keeps the references to selected filters and gets updated
            this.previousRequest = this.lastRequest ? JSON.parse(JSON.stringify(this.lastRequest)) : null;
            this.lastRequest = JSON.parse(JSON.stringify(this.getRequest()));
            this.refreshDataFunc(this.getRequest()).then(() => {
                this.isRefreshingData = false;
                this.lookupFiltersHandler.skipRefreshOfLookupsBecauseOfInit = false;
            }).catch(e => {
                this.isRefreshingData = false;
                if (typeof e === 'string') {
                    this.notificationService.show("MEP Error", e, NotificationType.Error);
                }
                else {
                    this.notificationService.show("MEP Error", "Error refreshing data", NotificationType.Error);
                }
            });
        }
    }

    getRequest(): MepDashboardRequest {
        return {
            portId: this.portId,
            vesselTypeIds: [],
            vesselTypeGroupIds: MepLookupItem.getIdsFromLookups(this.vesselTypeGroups, this.selectedVesselTypeGroups),
            emissionTypeIds: MepLookupItem.getIdsFromLookups(this.emissionTypes, this.selectedEmissionTypes),
            pointOfInterestIds: MepLookupItem.getIdsFromLookups(this.pointsOfInterests, this.selectedPointsOfInterest),
            operationModeIds: MepLookupItem.getIdsFromLookups(this.operationModes, this.selectedOperationModes),
            cargoTypeIds: MepLookupItem.getIdsFromLookups(this.cargoTypes, this.selectedCargoTypes),
            year: this.selectedYear ? this.selectedYear : null,
            months: this.selectedMonths ? this.selectedMonths.map(x => x.id) : null,
        };
    }

    clearAllFilters() {
        this.selectedEmissionTypes = [];
        this.selectedOperationModes = [];
        this.selectedCargoTypes = [];
        this.selectedPointsOfInterest = [];
        this.selectedVesselTypeGroups = [];
    }

    resetFiltersToDefault() {
        this.clearAllFilters();
        this.applyAllFilters();
    }
}